import { formatDuration, formatCurrency, translate } from '@gig/magic-common';
import { __awaiter, __generator } from 'tslib';
import { magic } from '@gig/magic-sdk';
import { CoreXLimitDuration, CoreXLimitType, CoreXGamingLimitType, CoreXLimitStatus } from '@gig/magic-sdk/dist/adapter/responsiblegaming/getUserTransactionLimits';
import { parse } from 'iso8601-duration';
import { returnLimitType } from '@gig/magic-sdk/dist/util/limits';

/**
 * Gets the translation key for a given limit type.
 * @param Limit Type
 */
function translateType(type) {
    switch (type) {
        case CoreXGamingLimitType.DEPOSIT:
            return 'view-limits.limit-type.deposit';
        case CoreXGamingLimitType.NET_DEPOSIT:
            return 'view-limits.limit-type.net-deposit';
        case CoreXGamingLimitType.WAGERING_CASINO:
            return 'view-limits.limit-type.wager_casino';
        case CoreXGamingLimitType.LOSS_CASINO:
            return 'view-limits.limit-type.loss_casino';
        case CoreXGamingLimitType.WITHDRAWAL:
            return 'view-limits.limit-type.withdrawal';
        case CoreXGamingLimitType.SESSION:
            return 'view-limits.limit-type.session';
        case CoreXGamingLimitType.WAGERING_SPORTS:
            return 'view-limits.limit-type.wagering_sports';
        case CoreXGamingLimitType.LOSS_SPORTS:
            return 'view-limits.limit-type.loss_sports';
        case CoreXGamingLimitType.MAX_WAGER_CASINO:
            return 'view-limits.limit-type.max-wager_casino';
        case CoreXGamingLimitType.MAX_WAGER_SPORTS:
            return 'view-limits.limit-type.max-wager_sports';
        default:
            console.warn('Limit type', type, 'translation not found.');
            return '';
    }
}
/**
 * Gets the translation key for a given period.
 * @param Period
 */
function translatePeriod(period) {
    switch (period) {
        case CoreXLimitDuration.ONE_DAY:
            return 'view-limits.period-type.daily';
        case CoreXLimitDuration.ONE_WEEK:
            return 'view-limits.period-type.weekly';
        case CoreXLimitDuration.ONE_MONTH:
            return 'view-limits.period-type.monthly';
        case CoreXLimitDuration.NO_DURATION:
            return 'view-limits.period-type.no-period';
        default:
            console.warn('Period type', period, 'translation not found.');
            return '';
    }
}
/**
 * Gets the translation key for a given status.
 * @param Status
 */
function translateStatus(status) {
    switch (status) {
        case CoreXLimitStatus.ACTIVE:
            return 'view-limits.activation-status.active';
        case CoreXLimitStatus.CANCELLED:
            return 'view-limits.activation-status.cancelled';
        case CoreXLimitStatus.PENDING_ACTIVATION:
            return 'view-limits.activation-status.pending-activation';
        case CoreXLimitStatus.PENDING_CANCELLATION:
            return 'view-limits.activation-status.pending-cancellation';
        case CoreXLimitStatus.PENDING_USER_ACTION:
            return 'view-limits.activation-status.pending-user-action';
        default:
            console.warn('Status type', status, 'translation not found.');
            return '';
    }
}
/**
 * Gets the formatted amount for the limit depending on the limit
 * type given.
 * @param amount Amount to be formatted
 * @param typeId Id of the limit type
 */
function formatLimitAmount(amount, typeId, currency) {
    if (typeId === CoreXLimitType.SESSION) {
        return formatDuration(amount);
    }
    else {
        return formatCurrency(amount, currency, 0);
    }
}
/**
 * Gets the translation key for a given duration.
 * @param Duration
 */
function translateDuration(duration) {
    var _a = parse(duration), years = _a.years, months = _a.months, weeks = _a.weeks, days = _a.days, hours = _a.hours, minutes = _a.minutes, seconds = _a.seconds;
    var text = '';
    if (years) {
        text += translate('view-limits.duration.years', { years: years }) + ' ';
    }
    if (months) {
        text += translate('view-limits.duration.months', { months: months }) + ' ';
    }
    if (weeks) {
        text += translate('view-limits.duration.weeks', { weeks: weeks }) + ' ';
    }
    if (days) {
        text += translate('view-limits.duration.days', { days: days }) + ' ';
    }
    if (hours) {
        text += translate('view-limits.duration.hours', { hours: hours }) + ' ';
    }
    if (minutes) {
        text += translate('view-limits.duration.minutes', { minutes: minutes }) + ' ';
    }
    if (seconds) {
        text += translate('view-limits.duration.seconds', { seconds: seconds }) + ' ';
    }
    return text.trim();
}
function getUserTransactionLimits() {
    return __awaiter(this, void 0, void 0, function () {
        var userTransactionLimits;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, magic.methods.responsibleGaming.getUserTransactionLimits()];
                case 1:
                    userTransactionLimits = _a.sent();
                    return [2 /*return*/, userTransactionLimits.map(function (limit) {
                            return {
                                id: limit.id,
                                amount: limit.amount,
                                createdOn: limit.createdOn,
                                period: limit.period,
                                type: returnLimitType(limit.type, limit.mediatorType),
                                activationStatus: limit.activationStatus,
                                pendingChangeExpectedOn: limit.pendingChangeExpectedOn,
                                isPendingCancellation: limit.isPendingCancellation,
                            };
                        })];
            }
        });
    });
}
function getMaxWagerLimits() {
    return __awaiter(this, void 0, void 0, function () {
        var maxWagerLimits;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, magic.methods.responsibleGaming.getMaxWagerLimits()];
                case 1:
                    maxWagerLimits = _a.sent();
                    return [2 /*return*/, maxWagerLimits.map(function (limit) {
                            return {
                                id: limit.id,
                                amount: limit.amount,
                                createdOn: limit.createdOn,
                                period: CoreXLimitDuration.NO_DURATION,
                                type: returnLimitType(CoreXLimitType.MAX_WAGER, limit.mediatorType),
                                activationStatus: limit.activationStatus,
                                pendingChangeExpectedOn: limit.pendingChangeExpectedOn,
                            };
                        })];
            }
        });
    });
}
function getUserSessionLimits() {
    return __awaiter(this, void 0, void 0, function () {
        var sessionLimits;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, magic.methods.responsibleGaming.getAllUserCumulativeSessionLimits()];
                case 1:
                    sessionLimits = _a.sent();
                    return [2 /*return*/, sessionLimits.map(function (limit) {
                            return {
                                id: limit.id,
                                duration: limit.duration,
                                createdOn: limit.createdOn,
                                period: limit.period,
                                type: CoreXGamingLimitType.SESSION,
                                activationStatus: limit.activationStatus,
                                pendingChangeExpectedOn: limit.pendingChangeExpectedOn,
                            };
                        })];
            }
        });
    });
}

export { formatLimitAmount, getMaxWagerLimits, getUserSessionLimits, getUserTransactionLimits, translateDuration, translatePeriod, translateStatus, translateType };
